import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert, Tooltip } from '@mui/material';
import { Button, StatusChip, Pagination } from '../../components/Common';
import { applicationsService, VendorApplication, ApplicationsPagination } from '../../services';
import { handleApiError, handleApiSuccess } from '../../services/error.service';
import { useSmartPageSize } from '../../config/pagination';

interface ApplicationsInfoRequestedProps {
  onDataChange?: () => void;
  applicationId?: string; // For filtering by specific applicationId from URL
}

const ApplicationsInfoRequested: React.FC<ApplicationsInfoRequestedProps> = ({ onDataChange, applicationId }) => {
  const [applications, setApplications] = useState<VendorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Smart pagination based on screen size
  const smartPageSize = useSmartPageSize('applications');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(smartPageSize);
  const [pagination, setPagination] = useState<ApplicationsPagination>({
    hasMore: false,
    currentPage: 1,
    totalItems: 0,
    totalPages: 1,
  });

  const fetchInfoRequestedApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await applicationsService.getInfoRequestedApplications(currentPage, itemsPerPage);
      setApplications(response.applications);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchInfoRequestedApplications();
  }, [currentPage, fetchInfoRequestedApplications]);

  const handleApprove = async (vendorId: string) => {
    try {
      setActionLoading(vendorId);
      await applicationsService.approveVendor(vendorId);
      handleApiSuccess('Vendor approved successfully');
      // Refresh the list - stay on current page or go to page 1 if current page is empty
      if (applications.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchInfoRequestedApplications();
      }
      // Notify parent to update badge counts
      if (onDataChange) {
        onDataChange();
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const time = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    return { date: `${day} ${month}`, time };
  };

  // Get the latest info request message
  const getLatestInfoRequest = (application: VendorApplication) => {
    if (!application.infoRequests || application.infoRequests.length === 0) {
      return null;
    }
    // Sort by timestamp descending and get the first one
    const sorted = [...application.infoRequests].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    return sorted[0];
  };

  // Filter applications based on applicationId if provided
  const filteredApplications = applicationId
    ? applications.filter(app => app.applicationId === applicationId || app.id === applicationId)
    : applications;
  
  // Show message if applicationId was provided but not found
  const applicationNotFound = applicationId && filteredApplications.length === 0 && !loading;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <CircularProgress sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', padding: '20px' }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Table Container with Scroll */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
            borderRadius: '4px',
          },
        }}
      >
        {/* Table */}
        <Box sx={{ backgroundColor: 'var(--palette-light-background-primary-value-hex)', width: '100%' }}>
          {/* Table Header - Desktop Only */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'row',
              alignSelf: 'stretch',
              gap: '16px',
              padding: '12px 0',
              borderBottom: '1px solid var(--palette-light-background-secondary-value-hex)',
              flexShrink: 0,
            }}
          >
            {/* Date */}
            <Box sx={{ width: '90px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Date
              </Typography>
            </Box>

            {/* Business */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Business
              </Typography>
            </Box>

            {/* Contact person */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Contact person
              </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ width: '200px', flexShrink: 0 }}></Box>
          </Box>

          {/* Table Content */}
          {applicationNotFound ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
                gap: '16px',
              }}
            >
              <Alert severity="info" sx={{ width: '100%', maxWidth: '600px' }}>
                Application with ID "{applicationId}" not found in info requested applications. It may be pending, approved, or does not exist.
              </Alert>
            </Box>
          ) : filteredApplications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
              }}
            >
              <Typography
                sx={{
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color: 'var(--palette-light-text-secondary-value-hex)',
                }}
              >
                No applications with info requested found
              </Typography>
            </Box>
          ) : (
            /* Table Rows */
            filteredApplications.map((application) => {
              const { date, time } = formatDate(application.submittedAt || application.createdAt);
              const latestInfoRequest = getLatestInfoRequest(application);
              
              return (
              <Box
                key={application.id}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignSelf: 'stretch',
                  gap: '16px',
                  padding: '16px 0',
                  borderBottom: '1px solid var(--palette-light-background-secondary-value-hex)',
                }}
              >
                {/* Date */}
                <Box sx={{ width: { xs: '100%', md: '90px' }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)', whiteSpace: 'pre-line' }}>
                    {`${date},\n${time}`}
                  </Typography>
                </Box>

                {/* Business */}
                <Box sx={{ flex: { xs: 'none', md: 1 }, minWidth: { xs: '100%', md: 0 }, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {application.businessName}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {application.category}
                  </Typography>
                  {application.hasLicense && (
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                      Trade license
                    </Typography>
                  )}
                </Box>

                {/* Contact person */}
                <Box sx={{ flex: { xs: 'none', md: 1 }, minWidth: { xs: '100%', md: 0 }, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {application.contactName}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {application.email}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {application.phone}
                  </Typography>
                </Box>

                {/* Status and Action */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '200px' },
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    alignItems: 'center',
                    gap: '8px',
                    paddingRight: '16px',
                  }}
                >
                  <Tooltip 
                    title={latestInfoRequest ? `Message: ${latestInfoRequest.message}` : 'Info requested'}
                    arrow
                  >
                    <Box>
                      <StatusChip status="warning" label="Info Requested" />
                    </Box>
                  </Tooltip>
                  <Button
                    onClick={() => handleApprove(application.id)}
                    disabled={actionLoading === application.id}
                    primary
                    size="content"
                    sx={{
                      padding: '0 10px',
                    }}
                  >
                    {actionLoading === application.id ? <CircularProgress size={16} /> : 'Approve'}
                  </Button>
                </Box>
              </Box>
            );
          }))}
        </Box>
      </Box>

      {/* Pagination */}
      {!loading && pagination.totalItems > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          itemLabel="applications"
          showItemCount
        />
      )}
    </Box>
  );
};

export default ApplicationsInfoRequested;
